import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/', label: language === 'es' ? 'Inicio' : 'Home' },
    { href: '/services', label: language === 'es' ? 'Servicios' : 'Services' },
    { href: '/service-areas', label: language === 'es' ? 'Áreas de Servicio' : 'Service Areas' },
    { href: '/projects', label: language === 'es' ? 'Proyectos' : 'Projects' },
    { href: '/payments', label: language === 'es' ? 'Pagos' : 'Payments' },
    { href: '/contact', label: language === 'es' ? 'Contacto' : 'Contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#003366]">
      <nav className="container mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
        {/* Logo - New IQES logo - 60% bigger */}
        <Link to="/" className="flex items-center">
          <img 
            src="/logo-iqes-new.png" 
            alt="IQES Low Voltage Solutions" 
            className="h-20 sm:h-24 object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium transition-colors ${
                location.pathname === link.href
                  ? 'text-orange-400'
                  : 'text-white hover:text-orange-400'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right side - Phone, Language, CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a 
            href="tel:+17869813760" 
            className="flex items-center gap-2 text-white text-sm hover:text-orange-400 transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>786-981-3760</span>
          </a>
          
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1 px-3 py-1.5 text-white text-sm border border-white/30 rounded-md hover:bg-white/10 transition-colors"
          >
            <Globe className="w-4 h-4" />
            <span>{language.toUpperCase()}</span>
          </button>

          <Link to="/quote">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 rounded-md">
              {language === 'es' ? 'Cotización' : 'Get Quote'}
            </Button>
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-white/10"
            style={{ backgroundColor: 'rgb(0, 51, 102)' }}
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block px-4 py-2 rounded-lg transition-colors ${
                    location.pathname === link.href
                      ? 'bg-orange-500 text-white'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <a 
                  href="tel:+17869813760" 
                  className="flex items-center gap-2 text-white px-4"
                >
                  <Phone className="w-4 h-4" />
                  786-981-3760
                </a>
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 text-white px-4"
                >
                  <Globe className="w-4 h-4" />
                  {language === 'es' ? 'English' : 'Español'}
                </button>
                <Link to="/quote" className="block px-4">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                    {language === 'es' ? 'Cotización' : 'Get Quote'}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
